import { A_LA_CARTE_SERVICE_ID, MISSING_VALUE } from '../shared/constants';
import { ServiceOption } from '../shared/types';
import {
  normalizeServiceList,
  readRole,
  readUpperHierarchy,
  toId,
  unwrapCollection,
  unwrapRelation,
} from '../shared/utils';

export const displayOrMissing = (value?: string | null) => {
  if (value == null || String(value).trim() === '') {
    return { text: MISSING_VALUE, missing: true };
  }
  return { text: String(value).trim(), missing: false };
};

export const getManagerName = (user: any) => {
  const manager = readUpperHierarchy(user?.upper_hierarchy);
  if (!manager) {
    return null;
  }
  const name = `${manager.firstname || ''} ${manager.lastname || ''}`.trim();
  return name || null;
};

export type ConsultantPrestation = ServiceOption & {
  beneficiariesCount: number;
};

export type ConsultantBeneficiary = {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  username?: string;
  isActivated?: boolean;
  services: ServiceOption[];
};

export type ConsultantPagination = {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
};

export const readAllowedServiceIds = (user: any): number[] =>
  unwrapRelation(user?.allowed_services)
    .map((item: any) => toId(item) ?? toId(item?.id))
    .filter((id: number | null): id is number => Boolean(id));

export const getRequiredServiceIdsFromPrestations = (
  prestations: ConsultantPrestation[]
): number[] =>
  Array.from(
    new Set(
      prestations
        .filter(item => Number(item.beneficiariesCount) > 0)
        .map(item => Number(item.id))
        .filter(id => id > 0)
    )
  );

export const getRequiredServiceIdsFromBeneficiaries = (
  beneficiaries: ConsultantBeneficiary[]
): number[] =>
  Array.from(
    new Set(
      beneficiaries.flatMap(item =>
        (item.services || []).map(service => Number(service.id)).filter(id => id > 0)
      )
    )
  );

export const staffHasRequiredServices = (staff: any, requiredIds: number[]): boolean => {
  const needed = requiredIds.filter(id => Number(id) !== A_LA_CARTE_SERVICE_ID);
  if (!needed.length) {
    return true;
  }
  const allowed = new Set(readAllowedServiceIds(staff));
  return needed.every(id => allowed.has(id));
};

export const mapConsultantPrestations = (payload: any): ConsultantPrestation[] => {
  const prestations: ConsultantPrestation[] = [];
  unwrapCollection(payload).forEach(item => {
    const id = Number(item?.id);
    if (!id) {
      return;
    }
    const count = Number(item.beneficiaries_count ?? item.beneficiariesCount);
    prestations.push({
      id,
      name: item.name || `Service ${id}`,
      beneficiariesCount: Number.isFinite(count) ? count : 0,
    });
  });
  return prestations;
};

export const mapConsultantBeneficiaries = (payload: any): ConsultantBeneficiary[] => {
  const beneficiaries: ConsultantBeneficiary[] = [];
  unwrapCollection(payload).forEach(item => {
    const id = Number(item?.id);
    if (!id) {
      return;
    }
    beneficiaries.push({
      id,
      firstname: item.firstname || '',
      lastname: item.lastname || '',
      email: item.email || '',
      username: item.username || '',
      isActivated: item.isActivated,
      services: normalizeServiceList(item.services),
    });
  });
  return beneficiaries;
};

export const readPaginationMeta = (
  payload: any,
  fallback: ConsultantPagination
): ConsultantPagination => {
  const pagination = payload?.meta?.pagination || {};
  const page = Number(pagination.page);
  const pageSize = Number(pagination.pageSize);
  const pageCount = Number(pagination.pageCount);
  const total = Number(pagination.total);
  return {
    page: page > 0 ? page : fallback.page,
    pageSize: pageSize > 0 ? Math.min(pageSize, 100) : fallback.pageSize,
    pageCount: Number.isFinite(pageCount) ? pageCount : 0,
    total: Number.isFinite(total) ? total : 0,
  };
};

export type ConsultantFormValues = {
  roleId: number | '';
  firstname: string;
  lastname: string;
  email: string;
  managerId: number | '';
};

export const userToConsultantForm = (user: any): ConsultantFormValues => {
  const role = readRole(user?.company_user_role);
  const manager = readUpperHierarchy(user?.upper_hierarchy);
  return {
    roleId: role?.id || '',
    firstname: user?.firstname || '',
    lastname: user?.lastname || '',
    email: user?.email || '',
    managerId: toId(manager) || '',
  };
};
